import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { MobileHeader } from './MobileNav';
import { ScreenSizeGate } from './ScreenSizeGate';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { TopbarSearch } from './TopbarSearch';
import { mobileAllowed, navItemForPath, visibleNav } from './nav';
import { useSmallScreen } from '../../lib/util/smallScreen';
import { useSession } from '../../lib/auth/useSession';
import { hasAnyAdminAccess } from '../../lib/auth/capabilities';
import { LoadingState } from '../../components/feedback/LoadingState';
import { CenteredPage } from '../../components/layout/CenteredPage';
import { Button } from '../../components/actions/Button';
import { logout, restoreSession } from '../../services/authService';
import { MfaGate } from '../../features/mfa/MfaGate';

const COLLAPSE_KEY = 'hp_a_sidebar_collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * The authenticated layout: turns session state into the right screen, then
 * renders the handoff's three-region shell — sidebar, sticky topbar, and a
 * scrolling content well (24px padding, 48px bottom, max 1440px, centred).
 * On a small screen (`smallScreen.ts`) the sidebar and topbar give way to a
 * header with the nav in a drawer (`MobileNav`), and a screen not turned on
 * for phones shows a notice instead (`ScreenSizeGate`).
 */
export function AdminShell() {
  const { isAuthenticated, status, profile, capabilities, can } = useSession();
  const location = useRouterState({ select: (s) => s.location });
  const navigate = useNavigate();
  const redirected = useRef(false);

  // Signed out while inside the shell (session restore found no usable
  // session, or a logout elsewhere). A `?s=` link is mid-handshake and must
  // stay put — SecretLoginHandler signs it in on this URL.
  const signedOut = !isAuthenticated && !new URLSearchParams(location.searchStr).has('s');

  // Redirect ONCE. A <Navigate> here re-fired on every re-render (its props
  // are a new object each time, and this component re-renders on every
  // location change), nesting /sign-in inside its own redirect until the tab
  // hung. The page to come back to is captured once and is never /sign-in.
  useEffect(() => {
    if (!signedOut || redirected.current) return;
    redirected.current = true;
    const back = location.pathname.startsWith('/sign-in') ? undefined : location.href;
    void navigate({ to: '/sign-in' as never, search: { redirect: back } as never, replace: true });
  }, [signedOut, location.pathname, location.href, navigate]);

  if (signedOut) return <LoadingState label="Going to sign in…" fill />;

  if (status === 'no-access') return <NoAdminAccess />;
  if (status === 'error') return <SessionError />;
  if (!profile || status !== 'ready') return <LoadingState label="Signing you in…" fill />;
  if (!hasAnyAdminAccess(capabilities)) return <NoAdminAccess />;

  return (
    <MfaGate>
      <Shell can={can} profile={profile} />
    </MfaGate>
  );
}

function Shell({
  can,
  profile,
}: {
  can: ReturnType<typeof useSession>['can'];
  profile: NonNullable<ReturnType<typeof useSession>['profile']>;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const small = useSmallScreen();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const active = navItemForPath(pathname);
  const isAccount = pathname === '/account';

  // A new page starts at the top. The content well, not the window, is what
  // scrolls (router scroll restoration is off — see router.tsx). Keyed on the
  // path only: filters, paging and dialogs change the search and keep the place.
  const mainRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    mainRef.current?.scrollTo?.({ top: 0 });
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- runs when the path changes
  }, [pathname]);

  const toggle = () =>
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, prev ? '0' : '1');
      } catch {
        /* storage unavailable — the choice just won't persist */
      }
      return !prev;
    });

  const content = (
    <ScreenSizeGate blocked={small && !mobileAllowed(pathname)}>
      <Outlet />
    </ScreenSizeGate>
  );

  if (small) {
    return (
      <div className="flex h-full flex-col overflow-hidden bg-page font-body">
        <MobileHeader
          breadcrumb={isAccount ? 'Account' : active?.breadcrumb}
          title={isAccount ? 'Your account' : active?.title}
          sections={visibleNav(can)}
          activeId={active?.id}
          profile={profile}
        />
        <main ref={mainRef} className="relative min-h-0 flex-1 overflow-y-auto">
          <div className="px-4 pb-10 pt-4">
            {/* No topbar on a phone: a list's search sits above it instead. */}
            {active?.mobile && active.searchLive && active.searchPlaceholder && pathname === active.path && (
              <div className="mb-3.5">
                <TopbarSearch key={pathname} placeholder={active.searchPlaceholder} />
              </div>
            )}
            {content}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden bg-page font-body">
      <Sidebar sections={visibleNav(can)} activeId={active?.id} collapsed={collapsed} onToggle={toggle} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          breadcrumb={isAccount ? 'Account' : active?.breadcrumb}
          title={isAccount ? 'Your account' : active?.title}
          search={
            // The search filters a list, so only on the list itself — not on a detail page below it.
            active?.searchLive && active.searchPlaceholder && pathname === active.path ? (
              <TopbarSearch key={pathname} placeholder={active.searchPlaceholder} />
            ) : undefined
          }
          profile={profile}
        />
        <main ref={mainRef} className="relative min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-well px-6 pb-12 pt-6">{content}</div>
        </main>
      </div>
    </div>
  );
}

function NoAdminAccess() {
  return (
    <CenteredPage
      title="No admin access"
      actions={
        <Button fullWidth onClick={() => void logout()}>
          Sign out
        </Button>
      }
    >
      This account has no HomePlated staff profile. If you should have access, ask an administrator to set one up.
    </CenteredPage>
  );
}

function SessionError(): ReactNode {
  const [retrying, setRetrying] = useState(false);
  return (
    <CenteredPage
      title="We couldn't load your session"
      actions={
        <>
          <Button
            fullWidth
            loading={retrying}
            onClick={async () => {
              setRetrying(true);
              await restoreSession();
              setRetrying(false);
            }}
          >
            Try again
          </Button>
          <Button fullWidth variant="ghost-dark" onClick={() => void logout()}>
            Sign out
          </Button>
        </>
      }
    >
      The server didn't answer. Check your connection and try again.
    </CenteredPage>
  );
}
